import React, { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Button,
  Modal,
  Form,
  Input,
  Tag,
  Row,
  Col,
  Statistic,
  Space,
  Tooltip,
  Menu,
  Typography,
  Empty,
  Popconfirm,
  message
} from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  HistoryOutlined,
  SaveOutlined,
  SendOutlined,
  LockOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import * as actions from 'store/actions/AuthActions';
import useUrlFilters from 'hooks/useUrlFilters';
import { CKEditor } from 'ckeditor4-react';
import { compactToolbar } from "utilities/config";

const { Sider, Content } = Layout;
const { Text } = Typography;

const generateCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 5; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

// Section and history pagination live in the URL (du_ prefix: this is a tab of My Profile).
const URL_FILTERS = {
  section: { type: 'string', default: 'today' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 10 },
};

const DailyUpdates = ({ isProfileLocked = false, employee }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState('add');
  const [selectedUpdate, setSelectedUpdate] = useState(null);
  const [form] = Form.useForm();
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [urlFilters, setUrlFilters] = useUrlFilters(URL_FILTERS, { prefix: 'du_' });
  const activeSection = urlFilters.section;
  const setActiveSection = (value) => setUrlFilters({ section: value });
  const [todayUpdate, setTodayUpdate] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  // CKEditor description states
  const [tasksCompleted, setTasksCompleted] = useState('');
  const [challenges, setChallenges] = useState('');
  const [planForTomorrow, setPlanForTomorrow] = useState('');

  // Confirmation code state
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmCode, setConfirmCode] = useState('');
  const [userInputCode, setUserInputCode] = useState('');

  // CKEditor config - same as TaskDrawer
  const baseUrl = (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');
  const editorConfig = {
    extraPlugins: ['tableresize', 'uploadimage'],
    removePlugins: ['exportpdf', 'forms', 'smiley', 'language', 'iframe', 'about', 'save', 'preview', 'print'],
    filebrowserImageUploadUrl: `${baseUrl}/template/upload_template_image/`,
    fileTools_requestHeaders: axios.defaults.headers.common,
    uploadUrl: `${baseUrl}/template/upload_template_image/`,
    contentsCss: [`${baseUrl}/staticfiles/ckeditor/4.19.0/full-all/document-style.css`, "/ckeditor-content.css"],
    bodyClass: 'document-editor',
    height: '150px',
    allowedContent: true,
    // Phones get a short toolbar; desktop keeps the full grouped one.
    ...compactToolbar(),
  };

  const todaySubmitted = todayUpdate?.status === 'SUBMITTED';

  // Fetch today's update
  const fetchTodayUpdate = async () => {
    try {
      const response = await actions.getTodayUpdate();
      setTodayUpdate(response);
    } catch (error) {
      setTodayUpdate(null);
    }
  };

  // Fetch update history
  const fetchUpdates = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const response = await actions.loadMyDailyUpdates(page, pageSize);
      if (response) {
        setUpdates(response.results || []);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: response.count || 0
        });
      }
    } catch (error) {
      console.error('Error fetching updates:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTodayUpdate();
  }, []);

  useEffect(() => {
    fetchUpdates(urlFilters.page, Math.min(urlFilters.pageSize, MAX_PAGE_SIZE));
  }, [urlFilters.page, urlFilters.pageSize]);

  // Calculate statistics
  const getWeeklyCount = () => {
    const weekStart = dayjs().startOf('week');
    return updates.filter(u => dayjs(u.update_date).isAfter(weekStart) && u.status === 'SUBMITTED').length;
  };

  const getMonthlyCount = () => {
    const monthStart = dayjs().startOf('month');
    return updates.filter(u => dayjs(u.update_date).isAfter(monthStart) && u.status === 'SUBMITTED').length;
  };

  // Handle modal open for add
  const handleAddUpdate = () => {
    setModalType('add');
    setSelectedUpdate(null);
    form.resetFields();
    setTasksCompleted('');
    setChallenges('');
    setPlanForTomorrow('');
    setIsModalVisible(true);
  };

  // Handle modal open for edit (only drafts)
  const handleEditUpdate = (record) => {
    if (record.status === 'SUBMITTED') return;
    setModalType('edit');
    setSelectedUpdate(record);
    setTasksCompleted(record.tasks_completed || '');
    setChallenges(record.challenges || '');
    setPlanForTomorrow(record.plan_for_tomorrow || '');
    setIsModalVisible(true);
  };

  // Handle delete
  const handleDeleteUpdate = async (id) => {
    try {
      const result = await actions.deleteDailyUpdate(id);
      if (result) {
        fetchTodayUpdate();
        fetchUpdates(pagination.current, pagination.pageSize);
      }
    } catch (error) {
      console.error('Error deleting update:', error);
    }
  };

  // Save handler (draft or submit)
  const handleSave = async (statusValue) => {
    try {
      if (!tasksCompleted || tasksCompleted === '<p>&nbsp;</p>' || tasksCompleted.replace(/<[^>]*>/g, '').trim() === '') {
        message.warning('Please describe your tasks completed');
        return;
      }
      setSubmitting(true);
      const data = {
        tasks_completed: tasksCompleted,
        challenges: challenges,
        plan_for_tomorrow: planForTomorrow,
        status: statusValue,
      };

      if (modalType === 'add') {
        await actions.createDailyUpdate(data);
      } else {
        await actions.updateDailyUpdate(selectedUpdate.id, data);
      }

      setIsModalVisible(false);
      setConfirmModalVisible(false);
      fetchTodayUpdate();
      fetchUpdates(pagination.current, pagination.pageSize);
    } catch (error) {
      if (error?.errorFields) return;
      console.error('Error saving update:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Submit with random code confirmation
  const handleSubmitConfirm = () => {
    if (!tasksCompleted || tasksCompleted.replace(/<[^>]*>/g, '').trim() === '') {
      message.warning('Please describe your tasks completed');
      return;
    }
    const code = generateCode();
    setConfirmCode(code);
    setUserInputCode('');
    setConfirmModalVisible(true);
  };

  const handleConfirmSubmit = () => {
    if (userInputCode === confirmCode) {
      handleSave('SUBMITTED');
    }
  };

  // Handle table pagination change
  const handleTableChange = (newPagination) => {
    // A new page size starts again from page 1
    const sizeChanged = newPagination.pageSize !== urlFilters.pageSize;
    setUrlFilters({ page: sizeChanged ? 1 : newPagination.current, pageSize: newPagination.pageSize });
  };

  const getStatusTag = (status) => {
    if (status === 'SUBMITTED') {
      return <Tag color="green">Submitted</Tag>;
    }
    return <Tag color="orange">Draft</Tag>;
  };

  // Table columns
  const columns = [
    {
      title: 'Date',
      dataIndex: 'update_date',
      key: 'update_date',
      width: 120,
      render: (date) => dayjs(date).format('MMM DD, YYYY'),
      sorter: (a, b) => dayjs(a.update_date).unix() - dayjs(b.update_date).unix(),
      defaultSortOrder: 'descend'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Tasks Completed',
      dataIndex: 'tasks_completed',
      key: 'tasks_completed',
      ellipsis: true,
      render: (html) => {
        const plainText = html?.replace(/<[^>]*>/g, '') || '';
        return (
          <Tooltip title={plainText.substring(0, 200)}>
            <span>{plainText.substring(0, 100)}{plainText.length > 100 ? '...' : ''}</span>
          </Tooltip>
        );
      }
    },
    {
      title: 'Challenges',
      dataIndex: 'challenges',
      key: 'challenges',
      width: 200,
      ellipsis: true,
      render: (html) => {
        const plainText = html?.replace(/<[^>]*>/g, '') || '';
        return plainText || <Text type="secondary">-</Text>;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => {
        const isSubmitted = record.status === 'SUBMITTED';
        return (
          <Space>
            {!isSubmitted && (
              <Tooltip title="Edit">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleEditUpdate(record)}
                />
              </Tooltip>
            )}
            {!isSubmitted && (
              <Popconfirm
                title="Delete this update?"
                onConfirm={() => handleDeleteUpdate(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Tooltip title="Delete">
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Tooltip>
              </Popconfirm>
            )}
            {isSubmitted && (
              <Tooltip title="Submitted">
                <LockOutlined style={{ color: '#52c41a' }} />
              </Tooltip>
            )}
          </Space>
        );
      }
    }
  ];

  // Expandable row to show full update content
  const expandedRowRender = (record) => (
    <div style={{ padding: '12px 0' }}>
      {record.tasks_completed && (
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ color: '#1890ff' }}>Tasks Completed</Text>
          <div style={{ marginTop: 4 }} dangerouslySetInnerHTML={{ __html: record.tasks_completed }} />
        </div>
      )}
      {record.challenges && (
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ color: '#faad14' }}>Challenges</Text>
          <div style={{ marginTop: 4 }} dangerouslySetInnerHTML={{ __html: record.challenges }} />
        </div>
      )}
      {record.plan_for_tomorrow && (
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ color: '#52c41a' }}>Plan for Tomorrow</Text>
          <div style={{ marginTop: 4 }} dangerouslySetInnerHTML={{ __html: record.plan_for_tomorrow }} />
        </div>
      )}
    </div>
  );

  // Sidebar menu items
  const menuItems = [
    {
      key: 'today',
      icon: <CalendarOutlined />,
      label: "Today's Update"
    },
    {
      key: 'history',
      icon: <HistoryOutlined />,
      label: 'Update History'
    }
  ];

  const getTodayStatusText = () => {
    if (!todayUpdate) return 'Pending';
    return todayUpdate.status === 'SUBMITTED' ? 'Submitted' : 'Draft';
  };

  const getTodayStatusColor = () => {
    if (!todayUpdate) return '#faad14';
    return todayUpdate.status === 'SUBMITTED' ? '#52c41a' : '#1890ff';
  };

  // Render today's update section
  const renderTodaySection = () => (
    <div style={{ padding: '0 24px 24px 24px' }}>
      {/* Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Today"
              value={getTodayStatusText()}
              valueStyle={{ color: getTodayStatusColor() }}
              prefix={todaySubmitted ? <CheckCircleOutlined /> : <CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="This Week"
              value={getWeeklyCount()}
              suffix="submitted"
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="This Month"
              value={getMonthlyCount()}
              suffix="submitted"
            />
          </Card>
        </Col>
      </Row>

      {/* Today's Update Card */}
      <Card
        title={
          <Space>
            <FileTextOutlined />
            <span>Today's Update - {dayjs().format('MMMM DD, YYYY')}</span>
            {todayUpdate && getStatusTag(todayUpdate.status)}
          </Space>
        }
        extra={
          !isProfileLocked && (
            todaySubmitted ? null : (
              todayUpdate ? (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => handleEditUpdate(todayUpdate)}
                >
                  Edit Update
                </Button>
              ) : (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddUpdate}
                >
                  Write Update
                </Button>
              )
            )
          )
        }
      >
        {todayUpdate ? (
          <div>
            {todayUpdate.tasks_completed && (
              <div style={{ marginBottom: '16px' }}>
                <Text strong>Tasks Completed:</Text>
                <div style={{ marginTop: '8px' }} dangerouslySetInnerHTML={{ __html: todayUpdate.tasks_completed }} />
              </div>
            )}
            {todayUpdate.challenges && (
              <div style={{ marginBottom: '16px' }}>
                <Text strong>Challenges:</Text>
                <div style={{ marginTop: '8px' }} dangerouslySetInnerHTML={{ __html: todayUpdate.challenges }} />
              </div>
            )}
            {todayUpdate.plan_for_tomorrow && (
              <div style={{ marginBottom: '16px' }}>
                <Text strong>Plan for Tomorrow:</Text>
                <div style={{ marginTop: '8px' }} dangerouslySetInnerHTML={{ __html: todayUpdate.plan_for_tomorrow }} />
              </div>
            )}
            {!todayUpdate.tasks_completed && !todayUpdate.challenges && !todayUpdate.plan_for_tomorrow && (
              <Text type="secondary">No content yet. Click Edit to add your update.</Text>
            )}
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 8 }}>
              Last saved: {dayjs(todayUpdate.updated_at).format('hh:mm A')}
            </Text>
          </div>
        ) : (
          <Empty
            description="No update for today"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            {!isProfileLocked && (
              <Button type="primary" onClick={handleAddUpdate}>
                Write Update
              </Button>
            )}
          </Empty>
        )}
      </Card>
    </div>
  );

  // Render history section
  const renderHistorySection = () => (
    <div style={{ padding: '0 24px 24px 24px' }}>
      <Card
        title={
          <Space>
            <HistoryOutlined />
            <span>Update History</span>
          </Space>
        }
        extra={
          !isProfileLocked && !todaySubmitted && !todayUpdate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddUpdate}
            >
              New Update
            </Button>
          )
        }
      >
        <Table
          columns={columns}
          dataSource={updates}
          rowKey="id"
          loading={loading}
          expandable={{
            expandedRowRender,
            rowExpandable: () => true,
          }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} updates`
          }}
          onChange={handleTableChange}
          size="middle"
        />
      </Card>
    </div>
  );

  return (
    <Layout style={{ background: 'transparent', height: '100%' }}>
      <Sider
        width={220}
        style={{
          background: '#fff',
          borderRight: '1px solid #f0f0f0'
        }}
      >
        <Menu
          mode="inline"
          selectedKeys={[activeSection]}
          style={{ height: '100%', borderRight: 0, paddingTop: '16px' }}
          items={menuItems}
          onClick={({ key }) => setActiveSection(key)}
        />
      </Sider>

      <Content style={{ background: '#f5f5f5', overflowY: 'auto' }}>
        <div style={{ padding: '24px 0' }}>
          {activeSection === 'today' && renderTodaySection()}
          {activeSection === 'history' && renderHistorySection()}
        </div>
      </Content>

      {/* Add/Edit Modal */}
      <Modal
        title={
          <Space>
            <span>{modalType === 'add' ? 'Daily Update' : 'Edit Daily Update'}</span>
            <Tag>{dayjs().format('DD MMM YYYY')}</Tag>
          </Space>
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={() => setIsModalVisible(false)}>
              Cancel
            </Button>
            <Space>
              <Button
                icon={<SaveOutlined />}
                onClick={() => handleSave('DRAFT')}
                loading={submitting}
              >
                Save as Draft
              </Button>
              {!todaySubmitted && (
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSubmitConfirm}
                  loading={submitting}
                >
                  Submit
                </Button>
              )}
            </Space>
          </div>
        }
        width={850}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Tasks Completed" required>
            <CKEditor
              initData={tasksCompleted}
              config={editorConfig}
              editorUrl={`${baseUrl}/staticfiles/ckeditor/4.19.0/full-all/ckeditor.js`}
              onChange={(event) => setTasksCompleted(event.editor.getData())}
            />
          </Form.Item>

          <Form.Item label="Challenges Faced (Optional)">
            <CKEditor
              initData={challenges}
              config={{ ...editorConfig, height: '120px' }}
              editorUrl={`${baseUrl}/staticfiles/ckeditor/4.19.0/full-all/ckeditor.js`}
              onChange={(event) => setChallenges(event.editor.getData())}
            />
          </Form.Item>

          <Form.Item label="Plan for Tomorrow (Optional)">
            <CKEditor
              initData={planForTomorrow}
              config={{ ...editorConfig, height: '120px' }}
              editorUrl={`${baseUrl}/staticfiles/ckeditor/4.19.0/full-all/ckeditor.js`}
              onChange={(event) => setPlanForTomorrow(event.editor.getData())}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Submit Confirmation Modal with random code */}
      <Modal
        title="Confirm Submission"
        open={confirmModalVisible}
        onCancel={() => setConfirmModalVisible(false)}
        onOk={handleConfirmSubmit}
        okText="Submit"
        okButtonProps={{ disabled: userInputCode !== confirmCode, loading: submitting }}
      >
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <Text>Once submitted, this update cannot be edited or re-submitted.</Text>
          <div style={{ margin: '20px 0' }}>
            <Text strong>Type the code below to confirm:</Text>
            <div style={{
              fontSize: '28px',
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: '6px',
              color: '#1890ff',
              background: '#f0f5ff',
              padding: '12px 24px',
              borderRadius: '8px',
              display: 'inline-block',
              margin: '12px 0',
              userSelect: 'none',
            }}>
              {confirmCode}
            </div>
          </div>
          <Input
            placeholder="Enter the code above"
            value={userInputCode}
            onChange={(e) => setUserInputCode(e.target.value.toUpperCase())}
            style={{ textAlign: 'center', fontSize: '18px', fontFamily: 'monospace', letterSpacing: '4px' }}
            maxLength={5}
            onPressEnter={() => {
              if (userInputCode === confirmCode) handleConfirmSubmit();
            }}
          />
          {userInputCode && userInputCode !== confirmCode && (
            <Text type="danger" style={{ display: 'block', marginTop: 8 }}>Code does not match</Text>
          )}
          {userInputCode === confirmCode && (
            <Text type="success" style={{ display: 'block', marginTop: 8 }}>Code matched!</Text>
          )}
        </div>
      </Modal>
    </Layout>
  );
};

export default DailyUpdates;
